import React from 'react';
import SEO from '../components/SEO';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, CheckCircle } from 'lucide-react';
import { services } from '../data/mock';

const ServicesPage = () => {
  const serviceCategories = [
    {
      title: "Security Guard Services",
      description: "Professional security personnel for businesses, events, and residential properties.",
      services: ["Armed Security", "Unarmed Security", "Event Security", "Executive Protection"]
    },
    {
      title: "Specialized Security",
      description: "Industry-specific security solutions tailored to unique requirements.",
      services: ["Hospital Security", "Hotel Security", "Retail Security", "Dispensary Security"]
    },
    {
      title: "Patrol Services",
      description: "Mobile and stationary patrol options for comprehensive coverage.",
      services: ["Vehicle Patrol", "Foot Patrol", "Bicycle Patrol", "Marked Patrol Units"]
    },
    {
      title: "Fire Watch & Safety",
      description: "Compliance-focused fire watch and safety monitoring services.",
      services: ["24-Hour Fire Watch", "Construction Site Security", "Hot Work Monitoring", "Emergency Response"]
    }
  ];

  return (
    <>
      <SEO 
        title="Security Services"
        description="Comprehensive security guard services including armed security, unarmed security, fire watch, event security, and commercial security. Professional guards available 24/7 nationwide."
        keywords="security services, armed security guards, unarmed security, fire watch services, event security, commercial security"
      />
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Our <span className="text-orange-500">Security Services</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              We offer comprehensive security solutions tailored to your specific needs. 
              From armed guards to specialized industry security, we have you covered.
            </p>
          </div>

          {/* Service Categories */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {serviceCategories.map((category) => (
              <div key={category.title} className="bg-slate-800 rounded-xl p-8 hover:bg-slate-750 transition-colors">
                <h3 className="text-2xl font-bold text-white mb-3">{category.title}</h3>
                <p className="text-gray-400 mb-6">{category.description}</p>
                <ul className="space-y-2">
                  {category.services.map((service) => (
                    <li key={service} className="flex items-center gap-2 text-gray-300">
                      <CheckCircle className="w-4 h-4 text-orange-500" />
                      {service}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* All Services Grid */}
      <section className="py-20 bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">All Security Services</h2>
            <p className="text-gray-400">Click on any service to learn more</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => (
              <Link 
                key={service.id}
                to={service.link}
                className="group bg-slate-900 rounded-xl overflow-hidden hover:shadow-2xl transition-all duration-300"
              >
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent"></div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-orange-400 transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-gray-400 text-sm line-clamp-2 mb-4">{service.description}</p>
                  <span className="inline-flex items-center gap-2 text-orange-500 font-medium text-sm group-hover:gap-3 transition-all">
                    Learn More <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Why Choose Our <span className="text-orange-500">Security Services?</span>
              </h2>
              <div className="space-y-4">
                {[
                  { title: "Licensed & Insured", desc: "All guards are fully licensed, bonded, and insured in their operating states." },
                  { title: "Background Checked", desc: "Rigorous background screening and verification for all security personnel." },
                  { title: "Professionally Trained", desc: "Comprehensive training programs covering all aspects of security operations." },
                  { title: "24/7 Support", desc: "Round-the-clock dispatch and management support for immediate response." },
                  { title: "Customized Solutions", desc: "Security plans tailored to your specific industry and requirements." },
                  { title: "Competitive Pricing", desc: "Quality security services at competitive rates with flexible contracts." }
                ].map((item) => (
                  <div key={item.title} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-orange-500/20 rounded-full flex items-center justify-center">
                        <CheckCircle className="w-5 h-5 text-orange-500" />
                      </div>
                    </div>
                    <div>
                      <h4 className="text-white font-semibold mb-1">{item.title}</h4>
                      <p className="text-gray-400 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1618371690240-e0d46eead4b8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHxzZWN1cml0eSUyMGd1YXJkfGVufDB8fHx8MTc2ODg5MjgxN3ww&ixlib=rb-4.1.0&q=85"
                alt="Professional Security"
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-orange-500 to-orange-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Secure Your Property?</h2>
          <p className="text-white/80 mb-8">Contact us today for a free consultation and custom security plan.</p>
          <Link 
            to="/contact"
            className="inline-flex items-center gap-2 bg-white text-orange-500 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors"
          >
            Get a Free Quote
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </>
  );
};

export default ServicesPage;